import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { OUTLOOK_REFUSALS, SHAREPOINT_REFUSALS, type DriveImportFailure } from "@gb/contracts";
import { ContentColumn } from "@/app/app-shell";
import { useProjectContext } from "@/app/project-context";
import { Button } from "@/components/ui/button";
import {
  openGooglePicker,
  useConnectGoogleDrive,
  preloadGoogleConnectors,
  useDriveArrivals,
  useGoogleDriveStatus,
  useImportFromDrive,
} from "@/features/project/connectors-api";
import {
  DriveImportArriving,
  DriveImportFailures,
} from "@/features/project/drive-import-failures";
import { useImports, useUploads } from "@/features/project/documents-api";
import { useSharepointStatus } from "@/features/project/sharepoint-api";
import { useOutlookStatus } from "@/features/project/outlook-api";
import { pickedFolder } from "@/lib/filed-folders";
import { useRoster } from "@/features/crew/crew-api";
import { ImportReviewBanner } from "@/features/review/import-banner";
import { useBrainSummary, useOrganiseProject } from "./brain-api";
import { DuplicatesLine } from "./duplicates-line";
import { UploadStatus } from "./upload-status";
import { OrganiseLine } from "./organise-line";
import { ShelfDialog } from "./shelf-dialog";
import { AddNoteDialog } from "./add-note-dialog";
import { BrainTabs } from "./brain-tabs";
import { Connectors, Shelves, type ShelfFilter } from "./source-shelves";
import { SharepointDialog } from "./sharepoint-dialog";
import { SharepointLine } from "./sharepoint-line";
import { OutlookDialog } from "./outlook-dialog";
import { OutlookLine } from "./outlook-line";

/**
 * The Site Brain's Sources tab: what this project holds, shelf by shelf.
 *
 * **A file is one row.** The corpus underneath holds one `knowledge_base` row
 * per *chunk* of a document, which is what makes a citation possible and what
 * made the old flat list unusable — a 75-page inverter manual arrived as 50–200
 * rows, all titled `SMA-manual.pdf`, each a fragment of a page. Chunks are
 * retrieval internals now and nothing renders them; a shelf lists stored
 * documents merged with the entries that are not documents.
 *
 * The project's documents are shelved by the tenant's categories
 * (`document-ai/0032`), and each shelf opens exactly the list it counted. Searching happens
 * inside a shelf, and still reaches inside a file: a hit on page 41 surfaces the
 * manual, says "page 41", and opens the viewer there.
 *
 * What that search is *not* is retrieval. It is a substring filter; semantic
 * answers need embeddings over `document_chunks`, deliberately not in the
 * schema yet. It says "Search" rather than "Ask" for that reason.
 */
export function BrainScreen() {
  const { projectId } = useProjectContext();

  const [adding, setAdding] = useState(false);
  const localInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  // Crew is citable evidence but lives outside `knowledge_base`, so its count
  // comes from the roster rather than the Brain summary.
  const roster = useRoster(projectId);
  // Which shelf is open over the page. The shelf owns its own search and paging
  // — see `shelf-dialog.tsx`.
  const [openShelf, setOpenShelf] = useState<{ filter: ShelfFilter; name: string } | null>(
    null,
  );
  const [driveError, setDriveError] = useState<string | null>(null);
  // Files the server could not fetch. Separate from `driveError`, which is the
  // whole flow having failed: these sit alongside documents that *did* arrive.
  const [driveFailures, setDriveFailures] = useState<DriveImportFailure[]>([]);

  const summary = useBrainSummary(projectId);
  const organise = useOrganiseProject(projectId);
  // Followed for what it re-reads: once an upload from here has been read,
  // its files have their categories, and the shelves above have to show them.
  useImports(projectId);

  // The one upload machine — presign, direct PUT, complete, with a
  // concurrency cap and retryable failures.
  //
  // Each drop is an import of its own (`document-ai/0019` §2): files added to a
  // live project later are a new bundle, not stirred into an earlier one.
  const { uploads, batch, add, retry, dismiss } = useUploads(projectId);

  const driveStatus = useGoogleDriveStatus();
  // The project's SharePoint folder (`plans/sharepoint-sync.md`).
  const sharepoint = useSharepointStatus(projectId);
  const [managingSharepoint, setManagingSharepoint] = useState(false);
  const sharepointBlocked = !sharepoint.data
    ? null
    : !sharepoint.data.configured
      ? SHAREPOINT_REFUSALS.notConfigured
      : !sharepoint.data.connected
        ? SHAREPOINT_REFUSALS.notConnected
        : null;
  // The project's Outlook folders (`plans/outlook-sync.md`).
  const outlook = useOutlookStatus(projectId);
  const [managingOutlook, setManagingOutlook] = useState(false);
  const outlookBlocked = !outlook.data
    ? null
    : !outlook.data.configured
      ? OUTLOOK_REFUSALS.notConfigured
      : !outlook.data.connected
        ? OUTLOOK_REFUSALS.notConnected
        : null;

  // Warm the Google scripts up now, so the Drive button's click still holds
  // the user activation Chrome needs to open a popup. See
  // `preloadGoogleConnectors`.
  useEffect(() => preloadGoogleConnectors(), []);
  const connectDrive = useConnectGoogleDrive();
  const importFromDrive = useImportFromDrive(projectId);
  const arrivals = useDriveArrivals(projectId);

  /**
   * Connect if needed, then pick.
   *
   * Connecting and picking are two grants — the server's refresh token and the
   * Picker's own browser token — so this always ensures the server side first;
   * otherwise a user could pick files the API then cannot download.
   */
  const onGoogleDrive = async (): Promise<void> => {
    setDriveError(null);
    setDriveFailures([]);
    const status = driveStatus.data;
    if (!status?.configured) return;

    try {
      if (!status.connected) await connectDrive.mutateAsync();
      if (!status.pickerApiKey || !status.clientId) {
        setDriveError("Google Drive isn’t fully configured for this workspace.");
        return;
      }
      const picked = await openGooglePicker({
        apiKey: status.pickerApiKey,
        clientId: status.clientId,
      });
      if (picked.length > 0) {
        // An import of its own, opened and closed by the server.
        const result = await importFromDrive.mutateAsync({ files: picked });
        setDriveFailures(result.failed);
        arrivals.follow(result, { joined: false });
      }
    } catch (error) {
      setDriveError(error instanceof Error ? error.message : "Google Drive failed.");
    }
  };

  const driveBusy = connectDrive.isPending || importFromDrive.isPending;

  return (
    <ContentColumn tight>
      <BrainTabs />

      {/* **What is happening and what waits, first** (2026-10-03, the owner).
          Uploads, refusals and the organising sat under the shelves, so a
          person who picked a file saw nothing happen. In order: the files
          being sent and the ones refused, Drive's, the organising, the one
          review line, the copies folded. */}
      <UploadStatus uploads={uploads} batch={batch} onRetry={retry} onDismiss={dismiss} />

      {driveError && (
        <p
          role="alert"
          className="mb-4 rounded-input border-[1.5px] border-danger/40 bg-danger-bg px-4 py-3 text-[0.85rem] font-semibold text-danger"
        >
          {driveError}
        </p>
      )}
      {/* Refused up front, then lost by the worker. Both halves, so the count
          is every picked file that did not arrive. */}
      <DriveImportFailures failures={[...driveFailures, ...arrivals.failures]} />
      <DriveImportArriving count={arrivals.arriving} className="mb-4" />

      {sharepoint.data?.link && (
        <SharepointLine
          projectId={projectId}
          link={sharepoint.data.link}
          checking={sharepoint.data.checking}
          onManage={() => setManagingSharepoint(true)}
        />
      )}

      {outlook.data && outlook.data.folders.length > 0 && (
        <OutlookLine
          projectId={projectId}
          folders={outlook.data.folders}
          checking={outlook.data.checking}
          onManage={() => setManagingOutlook(true)}
        />
      )}

      {/* The files no import has organised, and "Organise now" (RET-04). */}
      {summary.data && (
        <OrganiseLine
          organise={summary.data.organise}
          busy={organise.isPending}
          error={organise.error?.message ?? null}
          onOrganise={() => organise.mutate()}
        />
      )}

      <ImportReviewBanner projectId={projectId} className="mb-4" />

      <DuplicatesLine projectId={projectId} duplicates={summary.data?.duplicates ?? 0} className="mb-4" />

      {/* **The hero and the glance stats are gone**, and neither was replaced.
          The hero was a second ask box: Ask is a tab now, one segment away and
          always visible. The stat row counted the same corpus the list beneath
          it renders. The shelves carry the counts, where they change what a
          click does. */}

      <Connectors
        drive={{
          connected: driveStatus.data?.connected ?? false,
          configured: driveStatus.data?.configured ?? false,
          busy: driveBusy,
        }}
        onDrive={() => void onGoogleDrive()}
        sharepoint={{ linked: Boolean(sharepoint.data?.link), blocked: sharepointBlocked }}
        onSharepoint={() => setManagingSharepoint(true)}
        outlook={{ syncing: outlook.data?.folders.length ?? 0, blocked: outlookBlocked }}
        onOutlook={() => setManagingOutlook(true)}
        onLocal={() => localInputRef.current?.click()}
        onLocalFolder={() => folderInputRef.current?.click()}
      />

      <Shelves
        projectId={projectId}
        counts={summary.data?.bySourceType ?? {}}
        categories={summary.data?.categories ?? []}
        notSorted={summary.data?.notSorted ?? 0}
        unorganisedDocuments={summary.data?.unorganisedDocuments ?? 0}
        removedAtSource={summary.data?.removedAtSource ?? 0}
        needsDecision={summary.data?.needsDecision ?? 0}
        hidden={summary.data?.hidden ?? { total: 0, byStatus: {}, asked: {} }}
        crewCount={
          roster.data ? roster.data.onSite.length + roster.data.onboarding.length : null
        }
        active={openShelf?.filter}
        onSelect={(filter, name) => setOpenShelf(filter ? { filter, name } : null)}
      />

      {/* The connectors row's "Local files · Add more" opens this. */}
      <input
        ref={localInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) add(files);
          event.target.value = "";
        }}
      />
      {/* "Local files · Add a folder" opens this (the owner, 2026-10-04): every
          file in the folder, each with the folder it was in, as one import.
          `webkitdirectory` is not in React's attribute types, so it is set on
          the element; every browser we support has it. */}
      <input
        ref={(element) => {
          folderInputRef.current = element;
          element?.setAttribute("webkitdirectory", "");
        }}
        type="file"
        multiple
        className="hidden"
        aria-label="A folder to add"
        onChange={(event) => {
          const files = pickedFolder(Array.from(event.target.files ?? []));
          if (files.length > 0) add(files);
          event.target.value = "";
        }}
      />

      {/* **The dropzone, the search row and the flat list are gone.** Every one
          of them duplicated something above it: the dropzone offered the same
          four connectors the row already does, and the search-and-chips
          answered "everything, filtered" when the shelves *are* the filter — a
          reader had to scroll past them to see what clicking one had done.
          Browsing now happens inside the shelf, over the page.

          What survives is what nothing else does: a way to write a note by
          hand. Files still arriving moved to the top (2026-10-03). */}
      <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
        <Plus size={15} />
        Add a note
      </Button>

      {openShelf && (
        <ShelfDialog
          projectId={projectId}
          filter={openShelf.filter}
          title={openShelf.name}
          onClose={() => setOpenShelf(null)}
        />
      )}

      {adding && <AddNoteDialog projectId={projectId} onClose={() => setAdding(false)} />}
      {managingSharepoint && (
        <SharepointDialog
          projectId={projectId}
          link={sharepoint.data?.link ?? null}
          onClose={() => setManagingSharepoint(false)}
        />
      )}
      {managingOutlook && outlook.data && (
        <OutlookDialog projectId={projectId} status={outlook.data} onClose={() => setManagingOutlook(false)} />
      )}
    </ContentColumn>
  );
}
